/**
 * Sicherheits-kritisch: HTML-Mail-Inhalte werden vor der Anzeige gesäubert.
 *
 * Strategie:
 * - Browser-natives DOMParser für die Struktur
 * - Element-Allowlist (alles andere wird entfernt)
 * - Attribut-Allowlist pro Element
 * - Externe Bilder durch Tracking-Pixel-Schutz: src wird zu data-src,
 *   wird nur geladen wenn User auf "Bilder anzeigen" klickt
 * - Links: rel="noopener noreferrer", target="_blank"
 * - JS-URLs / data:-URLs in Links entfernt
 *
 * Wir nutzen KEIN externes DOMPurify als Dependency — eine eigene strikte
 * Whitelist-Implementierung ist hier sicherer und kleiner.
 */

const ALLOWED_TAGS = new Set([
  'a', 'b', 'i', 'u', 's', 'strong', 'em', 'small', 'span', 'p', 'br', 'hr',
  'div', 'ul', 'ol', 'li', 'dl', 'dt', 'dd',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'blockquote', 'pre', 'code', 'kbd', 'samp', 'q', 'cite', 'sub', 'sup',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption', 'colgroup', 'col',
  'img', 'figure', 'figcaption',
  'address', 'time', 'mark', 'ins', 'del',
  'font', // legacy aber häufig in Mails
]);

const GLOBAL_ATTRS = new Set(['class', 'style', 'title', 'dir']);

const TAG_ATTRS: Record<string, Set<string>> = {
  a: new Set(['href']),
  img: new Set(['alt', 'width', 'height']), // src wird gesondert behandelt
  td: new Set(['colspan', 'rowspan', 'align', 'valign', 'width', 'height', 'bgcolor']),
  th: new Set(['colspan', 'rowspan', 'align', 'valign', 'width', 'height', 'bgcolor']),
  table: new Set(['border', 'cellpadding', 'cellspacing', 'width', 'align', 'bgcolor']),
  tr: new Set(['align', 'valign', 'bgcolor']),
  font: new Set(['color', 'face', 'size']),
  col: new Set(['span', 'width']),
  colgroup: new Set(['span', 'width']),
};

// CSS-Properties die wir in style="" zulassen
const ALLOWED_CSS = new Set([
  'color', 'background-color', 'background', 'font-size', 'font-family', 'font-weight',
  'font-style', 'text-align', 'text-decoration', 'line-height', 'letter-spacing',
  'margin', 'margin-top', 'margin-bottom', 'margin-left', 'margin-right',
  'padding', 'padding-top', 'padding-bottom', 'padding-left', 'padding-right',
  'border', 'border-top', 'border-bottom', 'border-left', 'border-right',
  'border-color', 'border-style', 'border-width', 'border-radius',
  'width', 'height', 'min-width', 'max-width', 'min-height', 'max-height',
  'vertical-align', 'display', 'list-style', 'list-style-type',
]);

function isSafeUrl(url: string): boolean {
  const trimmed = url.trim().toLowerCase();
  if (!trimmed) return false;
  // Erlaubt: http(s), mailto, tel, relative
  if (trimmed.startsWith('javascript:') || trimmed.startsWith('vbscript:') || trimmed.startsWith('data:')) {
    return false;
  }
  return true;
}

function sanitizeStyle(style: string): string {
  return style
    .split(';')
    .map((decl) => decl.trim())
    .filter((decl) => {
      if (!decl) return false;
      const [prop] = decl.split(':');
      if (!prop) return false;
      const propName = prop.trim().toLowerCase();
      if (!ALLOWED_CSS.has(propName)) return false;
      // Verbotene Werte: url() mit nicht-https, expression()
      const value = decl.slice(prop.length + 1).toLowerCase();
      if (value.includes('javascript:') || value.includes('expression(')) return false;
      // url() nur https erlauben — aber für now: keine url() überhaupt (Tracking-Schutz)
      if (value.includes('url(')) return false;
      return true;
    })
    .join('; ');
}

function sanitizeNode(node: Node, blockExternalImages: boolean): Node | null {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.cloneNode(false);
  }
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }
  const el = node as Element;
  const tag = el.tagName.toLowerCase();
  if (!ALLOWED_TAGS.has(tag)) {
    // Element wird entfernt, aber Kinder werden weiter sanitisiert (in fragment)
    const frag = document.createDocumentFragment();
    el.childNodes.forEach((child) => {
      const s = sanitizeNode(child, blockExternalImages);
      if (s) frag.appendChild(s);
    });
    return frag;
  }

  const out = document.createElement(tag);

  for (const attr of Array.from(el.attributes)) {
    const name = attr.name.toLowerCase();
    const value = attr.value;

    // Globale Attribute
    if (GLOBAL_ATTRS.has(name)) {
      if (name === 'style') {
        const cleaned = sanitizeStyle(value);
        if (cleaned) out.setAttribute('style', cleaned);
      } else {
        out.setAttribute(name, value);
      }
      continue;
    }

    // Element-spezifische Attribute
    if (TAG_ATTRS[tag]?.has(name)) {
      out.setAttribute(name, value);
      continue;
    }

    // href
    if (tag === 'a' && name === 'href') {
      if (isSafeUrl(value)) {
        out.setAttribute('href', value);
        out.setAttribute('rel', 'noopener noreferrer');
        out.setAttribute('target', '_blank');
      }
      continue;
    }

    // img src — Tracking-Pixel-Schutz
    if (tag === 'img' && name === 'src') {
      if (!isSafeUrl(value)) continue;
      if (blockExternalImages) {
        // src wird zu data-src, lädt erst nach User-Klick auf "Bilder anzeigen"
        out.setAttribute('data-src', value);
        // 1x1 Platzhalter
        out.setAttribute(
          'src',
          'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxIiBoZWlnaHQ9IjEiLz4=',
        );
        out.classList.add('rla-blocked-image');
      } else {
        out.setAttribute('src', value);
      }
      continue;
    }

    // alle anderen Attribute werden ignoriert
  }

  // Kinder rekursiv
  el.childNodes.forEach((child) => {
    const s = sanitizeNode(child, blockExternalImages);
    if (s) out.appendChild(s);
  });

  return out;
}

export function sanitizeMailHtml(html: string, options: { showImages: boolean }): string {
  if (!html) return '';
  const parser = new DOMParser();
  // Wir nutzen text/html → result hat <html><head></head><body>...</body></html>
  const doc = parser.parseFromString(html, 'text/html');
  const cleanBody = document.createElement('div');
  doc.body.childNodes.forEach((child) => {
    const s = sanitizeNode(child, !options.showImages);
    if (s) cleanBody.appendChild(s);
  });
  return cleanBody.innerHTML;
}
